/**
 * 별자리를 캔버스에 그린다 (1080×1350, 인스타 세로 규격).
 *
 * renderYearTrail 과 같은 이유로 SVG 를 쓰지 않는다 — 브라우저가 SVG 를
 * 이미지로 바꿀 때 외부 폰트를 안 불러와 한글이 깨진다. 캔버스에 직접
 * 그리면 페이지에 이미 로드된 웹폰트를 그대로 쓴다.
 */

/**
 * 두 규격으로 그린다.
 *
 *   feed   1080×1350 (4:5)  인스타 게시물
 *   story  1080×1920 (9:16) 인스타 스토리
 *
 * 스토리는 위아래 240px 쯤이 인스타 자기 UI(프로필·답장 칸)에 덮이므로
 * 글자를 그 안쪽으로 들여 놓는다. 게시물 규격을 스토리에 올리면 위아래가
 * 남아 인스타가 알아서 늘리거나 여백을 채운다 — 그게 싫어서 나눈다.
 */
export const SIZES = {
  feed: {
    key: "feed", label: "4:5", w: 1080, h: 1350,
    capY: 128, nameY: 208, subY: 256, countY: 306,
    artTop: 372, artBot: 852, listMid: 1030, signY: 1258,
  },
  story: {
    key: "story", label: "9:16", w: 1080, h: 1920,
    capY: 300, nameY: 384, subY: 434, countY: 486,
    artTop: 560, artBot: 1180, listMid: 1380, signY: 1640,
  },
};

/** 기본은 게시물 규격 */
export const CANVAS_W = SIZES.feed.w;
export const CANVAS_H = SIZES.feed.h;

const VOID = "#04050f", STAR = "#f5f0e8", GOLD = "#c9a84c", WARM = "#e8d5b0";
const HAN = "'Noto Sans KR', sans-serif";
const LAT = "'Cormorant Garamond', serif";

/** 같은 그림이 늘 같게 나오도록 배경 별 자리를 고정한다 */
function rng(seed) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export async function waitForFonts() {
  if (!document.fonts) return;
  const specs = [`700 56px "Noto Sans KR"`, `400 28px "Noto Sans KR"`, `300 30px "Cormorant Garamond"`];
  await Promise.all(specs.map((s) => document.fonts.load(s).catch(() => {})));
  await document.fonts.ready.catch(() => {});
}

function text(ctx, s, x, y, { font, fill, align = "left", alpha = 1 }) {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = fill;
  ctx.textAlign = align;
  ctx.globalAlpha = alpha;
  ctx.fillText(s, x, y);
  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{tpl, fill, name}} data  tpl=별자리 틀, fill=채울 책, name=사람 이름
 */
export function drawConstellation(canvas, { tpl, fill, name, size = "feed" }) {
  const L = SIZES[size] ?? SIZES.feed;
  const CW = L.w, CH = L.h;
  const ctx = canvas.getContext("2d");
  canvas.width = CW;
  canvas.height = CH;

  // ── 배경 ──
  const bg = ctx.createRadialGradient(CW / 2, CH * 0.42, 0, CW / 2, CH * 0.42, CW);
  bg.addColorStop(0, "#0d1030");
  bg.addColorStop(1, VOID);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CW, CH);

  const r = rng(2718);
  const dust = Math.round(110 * (CH / SIZES.feed.h));
  for (let i = 0; i < dust; i++) {
    ctx.globalAlpha = r() * 0.4 + 0.08;
    ctx.fillStyle = r() > 0.88 ? "#c8b6ff" : STAR;
    ctx.beginPath();
    ctx.arc(r() * CW, r() * CH, r() * 1.8 + 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.textBaseline = "alphabetic";

  // ── 제목 ──
  text(ctx, "CONSTELLATION", CW / 2, L.capY, { font: `300 28px ${LAT}`, fill: GOLD, align: "center", alpha: 0.75 });
  text(ctx, tpl.name, CW / 2, L.nameY, { font: `700 60px ${HAN}`, fill: STAR, align: "center" });
  text(ctx, tpl.sub, CW / 2, L.subY, { font: `400 27px ${HAN}`, fill: STAR, align: "center", alpha: 0.42 });
  /* 아래 서명 자리에 두었더니 별 7개짜리 목록의 마지막 줄과 겹쳤다.
     제목 밑이 읽는 순서에도 맞는다. */
  text(ctx, `별 ${tpl.need}개를 ${name}님이 읽은 책으로 채웠습니다`,
    CW / 2, L.countY, { font: `400 28px ${HAN}`, fill: GOLD, align: "center", alpha: 0.55 });

  // ── 별판 ──
  const PAD_X = 170, TOP = L.artTop, BOT = L.artBot;
  const W = CW - PAD_X * 2, H = BOT - TOP;
  const pts = tpl.points.map(([x, y]) => [PAD_X + x * W, TOP + y * H]);

  // 선
  ctx.strokeStyle = "rgba(201,168,76,.45)";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.beginPath();
  for (const [a, b] of tpl.edges) {
    ctx.moveTo(pts[a][0], pts[a][1]);
    ctx.lineTo(pts[b][0], pts[b][1]);
  }
  ctx.stroke();

  // 별 · 번호
  //
  // 제목을 별 옆에 바로 적어봤는데 못 쓴다. 북두칠성은 별이 120px
  // 간격인데 한글 제목은 250px 까지 가서, 일곱 개 중 셋이 서로 파고들고
  // 오리온의 허리 세 별은 하나가 아예 가려졌다. 별에는 번호만 붙이고
  // 제목은 아래 목록으로 내린다 — 연간 궤적이 쓰는 방식과 같다.
  pts.forEach(([x, y], i) => {
    const glow = ctx.createRadialGradient(x, y, 0, x, y, 50);
    glow.addColorStop(0, "rgba(201,168,76,.42)");
    glow.addColorStop(1, "rgba(201,168,76,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, 50, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = STAR;
    ctx.beginPath();
    ctx.arc(x, y, 11, 0, Math.PI * 2);
    ctx.fill();

    // 숫자는 한글 글꼴로 쓴다. Cormorant Garamond 는 1 을 I 로 그린다.
    text(ctx, String(i + 1), x + 24, y - 16, { font: `400 26px ${HAN}`, fill: GOLD, alpha: 0.9 });
  });

  /** 글자가 칸을 넘으면 잘라내고 '…' 를 붙인다 */
  const fit = (str, font, maxW) => {
    ctx.font = font;
    let t = String(str ?? "");
    if (!t || ctx.measureText(t).width <= maxW) return t;
    while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
    return t + "…";
  };

  // ── 목록 ──
  // 별 수가 3~9 로 다르다. 목록을 늘 같은 자리에서 시작하면 3별짜리는
  // 아래가 텅 비므로, 정해둔 띠 안에서 가운데로 맞춘다.
  const LINE = 32;
  const bandMid = L.listMid;
  let ly = bandMid - (fill.length * LINE) / 2 + 24;
  const LX = 250;
  fill.forEach((b, i) => {
    text(ctx, String(i + 1), LX - 34, ly, { font: `400 25px ${HAN}`, fill: GOLD, align: "right", alpha: 0.85 });
    text(ctx, fit(b.title, `400 27px ${HAN}`, 560), LX, ly, { font: `400 27px ${HAN}`, fill: WARM });
    ly += LINE;
  });

  // ── 서명 ──
  text(ctx, "MUROO", CW / 2, L.signY, { font: `300 46px ${LAT}`, fill: GOLD, align: "center", alpha: 0.85 });
  text(ctx, "muroo.kr", CW / 2, L.signY + 42, { font: `300 26px ${LAT}`, fill: STAR, align: "center", alpha: 0.35 });
}
